import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const validSlug = slug || 'all';
  const topRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(validSlug);

  useEffect(() => {
    setActiveTab(validSlug);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [validSlug]);

  const filteredComics = useMemo(() => {
    if (activeTab === 'all') return comics;
    return comics.filter((c) => c.category === activeTab);
  }, [activeTab]);

  const currentCategory = categories.find((c) => c.slug === activeTab);

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 43423漫画网`
    : '全部漫画 - 43423漫画网';

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto" ref={topRef}>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: currentCategory?.name || '全部' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {currentCategory ? currentCategory.name : '全部漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {activeTab === 'all'
                ? '下拉式漫画大全，好看的漫画下拉式在线看，全部漫画资源尽在这里'
                : `下拉式${currentCategory?.name || '漫画'}免费阅读，漫画下拉式在线阅读体验`}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => {
                  setActiveTab(cat.slug);
                  topRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </button>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 mb-12" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  tags={comic.tags}
                  status={comic.status}
                  rating={comic.rating}
                  views={comic.views}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画资源</p>
              <Link to="/category/all" className="text-primary-500 hover:text-primary-600 text-sm mt-3 inline-block">
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: `43423漫画网${currentCategory?.name || '全部'}漫画分类，提供下拉式漫画免费在线阅读。`,
            url: `https://43423mh.com/category/${activeTab}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '43423漫画网',
              url: 'https://43423mh.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://43423mh.com/' },
                { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://43423mh.com/category/all' },
                { '@type': 'ListItem', position: 3, name: currentCategory?.name || '全部' },
              ],
            },
          }),
        }}
      />
    </>
  );
}